import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';
import parseDuration from 'parse-duration';

import NewEventType from 'app/src/components/EventTypes/new';
import NewMedicine from 'app/src/components/Medicines/new';
import { SUGGEST_MEDICINES } from 'app/src/components/Medicines/queries';
import NewProvider from 'app/src/components/Providers/new';
import { SUGGEST_PROVIDERS } from 'app/src/components/Providers/queries';
import NewThing from 'app/src/components/Things/new';
import { Fields, defaultValues, prepareInput, useFormSubmit } from 'app/src/elements/forms';
import { Button } from 'app/src/elements/buttons';
import { Loading } from 'app/src/elements/DataState';
import { Notice } from 'app/src/elements/Notice';
import Modal from 'app/src/elements/Modal';
import { Title } from 'app/src/styles';
import { useNavigate, useOutletContext } from 'app/src/utils/routing';

import EventThings from './EventThings';
import { nodes } from './index';
import { CREATE_EVENT, EVENT_SELECTS } from './queries';

export const TIME_TYPES = [
  { value: 'created_at', label: 'Time Info Entered', },
  { value: 'date_only', label: 'Date Only', },
  { value: 'yesterday', label: 'Yesterday', },
  { value: 'exact', label: 'Exact Time', },
  { value: 'estimated', label: 'Estimated Time', },
];

const DURATION_MESSAGE =
  'Enter duration in a format like 1h 20m or like 20 minutes, 5 seconds';

export const FIELDS = {
  eventTypeId: {
    label: 'Event Type',
    rules: { required: true },
  },
  timeType: {
    label: 'Time Type',
    rules: { required: true },
    select: TIME_TYPES,
  },
  eventAt: {
    label: 'Time',
    type: 'datetime',
    rules: { required: true },
  },
  eventOn: {
    label: 'Date',
    type: 'date',
  },
  clientId: {
    label: 'Client',
    type: 'hidden',
    rules: { required: true },
  },
  notes: {
    label: 'Notes',
  },
  amount: {
    label: 'Amount/Level/Value',
    type: 'hidden',
    blank: 'null',
  },
  duration: {
    label: 'Duration',
    type: 'hidden',
    rules: {
      validate: value => {
        if (value && !parseDuration(value)) {
          return DURATION_MESSAGE;
        }
      }
    }
  },
};

const NewEvent = () => {
  const [eventMedicines, setEventMedicines] = useState({});
  const [eventProviders, setEventProviders] = useState({});
  const [eventThings, setEventThings] = useState({});
  const [medicineGroupId, setMedicineGroupId] = useState(null);
  const [showNewEventTypeModal, setShowNewEventTypeModal] = useState(false);
  const [showNewMedicineModal, setShowNewMedicineModal] = useState(false);
  const [showNewProviderModal, setShowNewProviderModal] = useState(false);
  const [showNewThingModal, setShowNewThingModal] = useState(false);
  const [addedMedicines, setAddedMedicines] = useState([]);
  const [addedProviders, setAddedProviders] = useState([]);
  const [addedThings, setAddedThings] = useState([]);

  const navigate = useNavigate();
  const client = useOutletContext()?.client;

  const {
    control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: {
        timeType: 'created_at',
        eventAt: DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm"),
        client,
      },
    }),
  });

  const { data: selects, refetch: refetchSelects } = useQuery(EVENT_SELECTS, {
    fetchPolicy: 'cache-and-network'
  });

  const { submit: createEvent, formStatus, errorMessage } =
    useFormSubmit(CREATE_EVENT, {
      onCompleted: () => {
        const eventsPath = client ? `/clients/${client.id}/events` : '/events';
        navigate(eventsPath);
      }
    });

  const timeType = watch('timeType');

  useEffect(() => {
    if ('date_only' === timeType) {
      setValue('eventOn', DateTime.now().toFormat('yyyy-MM-dd'));
    }
  }, [timeType]);

  useEffect(() => {
    // Auto select client if only 1 option or if there is a default client
    if (selects) {
      if (1 === selects.clients.edges.length) {
        setValue('clientId', selects.clients.edges[0].node.id);
      } else {
        const defaultClient =
          selects.clients.edges.find(({ node }) => node.userDefault);

        if (defaultClient) {
          setValue('clientId', defaultClient.node.id);
        }
      }
    }
  }, [selects]);

  const onSubmit = ({ eventOn, ...otherInput }) => {
    const input = prepareInput(otherInput, FIELDS);

    if ('created_at' === input.timeType) {
      input.eventAt = DateTime.now().toISO();
    } else if ('yesterday' === input.timeType) {
      input.eventAt = DateTime.now().minus({ days: 1 }).startOf('day').set({ hour: 8 }).toISO();
      input.timeType = 'date_only';
    } else if ('date_only' === input.timeType) {
      input.eventAt = DateTime.fromISO(`${eventOn}T08:00`).toISO();
    }

    if (otherInput.duration) {
      otherInput.duration = parseDuration(otherInput.duration, 's');
    } else {
      otherInput.duration = null;
    }

    if (medicineGroupId) {
      input.medicineGroupId = medicineGroupId;
    }

    input.eventMedicinesAttributes = Object.keys(eventMedicines)
      .map(medicineId => eventMedicines[medicineId])
      .filter(eventMedicine => eventMedicine.selected)
      .map(eventMedicine => (
        { medicineId: eventMedicine.thingId, notes: eventMedicine.notes }
      ));

    input.eventProvidersAttributes = Object.keys(eventProviders)
      .map(providerId => eventProviders[providerId])
      .filter(eventProvider => eventProvider.selected)
      .map(eventProvider => (
        { providerId: eventProvider.thingId, notes: eventProvider.notes }
      ));

    input.eventThingsAttributes = Object.keys(eventThings)
      .map(thingId => eventThings[thingId])
      .filter(eventThing => eventThing.selected)
      .map(eventThing => (
        { thingId: eventThing.thingId, notes: eventThing.notes }
      ));

    createEvent({ variables: { input } });
  };

  const eventTypeCreated = createdEventType => {
    refetchSelects();
    setValue('eventTypeId', createdEventType.id);
    setShowNewEventTypeModal(false);
  };

  const medicineCreated = createdMedicine => {
    setAddedMedicines([...addedMedicines, createdMedicine]);
    setEventMedicines({
      ...eventMedicines,
      [createdMedicine.id]: { thingId: createdMedicine.id, selected: true }
    });
    setShowNewMedicineModal(false);
  };

  const providerCreated = createdProvider => {
    setAddedProviders([...addedProviders, createdProvider]);
    setEventProviders({
      ...eventProviders,
      [createdProvider.id]: { thingId: createdProvider.id, selected: true }
    });
    setShowNewProviderModal(false);
  };

  const thingCreated = createdThing => {
    setAddedThings([...addedThings, createdThing]);
    setEventThings({
      ...eventThings,
      [createdThing.id]: { thingId: createdThing.id, selected: true }
    });
    setShowNewThingModal(false);
  };

  const handleGroupSelect = group => {
    if (medicineGroupId === group.id) {
      // deselect group — clear individual selections that came from this group
      setMedicineGroupId(null);
      const cleared = { ...eventMedicines };
      group.medicines.forEach(m => {
        if (cleared[m.id]?.fromGroup) {
          cleared[m.id] = { thingId: m.id, selected: false };
        }
      });
      setEventMedicines(cleared);
    } else {
      setMedicineGroupId(group.id);
      const updated = { ...eventMedicines };
      group.medicines.forEach(m => {
        updated[m.id] = { thingId: m.id, selected: true, fromGroup: true };
      });
      setEventMedicines(updated);
    }
  };

  const fields = { ...FIELDS };

  if ('date_only' === timeType) {
    fields.eventAt = { ...fields.eventAt, type: 'hidden', rules: { } };
    fields.eventOn = {
      ...fields.eventOn,
      type: 'date',
      rules: { required: true }
    };
  } else if ('created_at' === timeType || 'yesterday' === timeType) {
    fields.eventAt = { ...fields.eventAt, type: 'hidden', rules: { } };
    fields.eventOn = { ...fields.eventOn, type: 'hidden', rules: { } };
  } else {
    fields.eventAt = {
      ...fields.evenAt,
      type: 'datetime',
      rules: { required: true }
    };
    fields.eventOn = { ...fields.eventOn, type: 'hidden', rules: { } };
  }

  if (selects) {
    fields.clientId = { ...fields.clientId };

    if (!client) {
      fields.clientId.type = null;
      fields.clientId.select = [
        { value: '', label: 'Select' },
        ...selects.clients.edges
          .map(edge => ({ value: edge.node.id, label: edge.node.name }))
      ];
    }

    fields.eventTypeId = {
      ...fields.eventTypeId,
      type: 'button',
      options: selects.eventTypes.edges.map(edge => edge.node),
      add: () => setShowNewEventTypeModal(true),
    };
  }

  const eventType = selects?.eventTypes.edges
    .find(edge => edge.node.id === watch('eventTypeId'))?.node;

  const clientId = watch('clientId');
  const medicineGroups = selects?.medicineGroups?.edges
    .map(edge => edge.node)
    .filter(group => group.client.id === clientId) || [];

  if (eventType) {
    if (eventType.hasAmount) {
      fields.amount = { ...fields.amount, type: 'Int' };
    }

    if (eventType.amountLabel) {
      fields.amount = { ...fields.amount, label: eventType.amountLabel };
    }

    if (eventType.hasDuration) {
      fields.duration = { ...fields.duration, type: 'string' };
    }
  }

  return (
    <View>
      <Title>Add Event{client && ` for ${client.name}`}</Title>

      <Fields
        fields={fields}
        control={control}
        errors={errors}
      />

      {eventType?.hasMedicines && (
        <EventThings
          title="Medicines"
          eventThings={eventMedicines}
          setEventThings={setEventMedicines}
          watch={watch}
          showButtons
          add={() => setShowNewMedicineModal(true)}
          addedThings={addedMedicines}
          query={SUGGEST_MEDICINES}
          groups={medicineGroups}
          selectedGroupId={medicineGroupId}
          onGroupSelect={handleGroupSelect}
        />
      )}

      {eventType?.hasProviders && (
        <EventThings
          title="Providers"
          eventThings={eventProviders}
          setEventThings={setEventProviders}
          watch={watch}
          showButtons
          add={() => setShowNewProviderModal(true)}
          addedThings={addedProviders}
          query={SUGGEST_PROVIDERS}
        />
      )}

      {eventType?.hasThings && (
        <EventThings
          eventThings={eventThings}
          setEventThings={setEventThings}
          watch={watch}
          showButtons
          add={() => setShowNewThingModal(true)}
          addedThings={addedThings}
        />
      )}

      {errorMessage && <Notice type="error">{errorMessage}</Notice>}

      {'submitting' === formStatus ? (
        <Loading />
      ) : (
        <Button label="Save" onPress={handleSubmit(onSubmit)} wide />
      )}

      {showNewEventTypeModal && (
        <Modal title="Add Event Type" setShow={setShowNewEventTypeModal}>
          <NewEventType onCreated={eventTypeCreated} />
        </Modal>
      )}

      {showNewMedicineModal && (
        <Modal title="Add Medicine" setShow={setShowNewMedicineModal}>
          <NewMedicine
            onCreated={medicineCreated}
            clientId={watch().clientId}
          />
        </Modal>
      )}

      {showNewProviderModal && (
        <Modal title="Add Provider" setShow={setShowNewProviderModal}>
          <NewProvider
            onCreated={providerCreated}
            clientId={watch().clientId}
          />
        </Modal>
      )}

      {showNewThingModal && (
        <Modal title="Add Thing" setShow={setShowNewThingModal}>
          <NewThing onCreated={thingCreated} />
        </Modal>
      )}
    </View>
  );
};

export default NewEvent;
